import { queryOptions, skipToken } from '@tanstack/react-query';

import { shouldRetry } from '../api';

import { fetchMePayload } from './api/me.api';
import { fetchSiteAccess } from './api/siteAccess.api';
import { normalizeMe } from './api/normalizeMe';
import { ACCESS_GC, ACCESS_STALE, SITE_ACCESS_STALE, accessKeys } from './query-keys';
import type { SiteAccess } from './types';

export const meAccessQueryOptions = () =>
  queryOptions({
    queryKey: accessKeys.me(),
    queryFn: ({ signal }) => fetchMePayload(signal),
    select: normalizeMe,
    staleTime: ACCESS_STALE,
    gcTime: ACCESS_GC,
    retry: (failureCount: number, error: unknown) => failureCount < 1 && shouldRetry(failureCount, error),
  });

export const SITE_ACCESS_BATCH = 200;

export const siteAccessBatchesOf = (plantIds: readonly string[]): readonly (readonly string[])[] => {
  const unique = [...new Set(plantIds)].sort();
  const batches: (readonly string[])[] = [];
  for (let start = 0; start < unique.length; start += SITE_ACCESS_BATCH) batches.push(unique.slice(start, start + SITE_ACCESS_BATCH));
  return batches;
};

export const siteAccessQueryOptions = (plantIds: readonly string[]) =>
  queryOptions({
    queryKey: accessKeys.siteAccess(plantIds),
    queryFn: plantIds.length > 0 ? ({ signal }) => fetchSiteAccess(plantIds, signal) : skipToken,
    staleTime: SITE_ACCESS_STALE,
    gcTime: ACCESS_GC,
  });

const noAccess = (plantId: string): SiteAccess => ({
  plantId,
  canView: false,
  canEditNotifications: false,
  canEditPlant: false,
  canManagePeople: false,
});

const accessOf =
  (plantId: string) =>
  (plants: readonly SiteAccess[]): SiteAccess =>
    plants.find((access) => access.plantId === plantId) ?? noAccess(plantId);

export const plantSiteAccessQueryOptions = (plantId: string | undefined) =>
  queryOptions({ ...siteAccessQueryOptions(plantId ? [plantId] : []), select: accessOf(plantId ?? '') });
